import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './WhitelistManager.css';

function WhitelistManager() {
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  
  const [newEmail, setNewEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [bulkEmails, setBulkEmails] = useState('');

  useEffect(() => {
    fetchAllowedEmails();
  }, []);

  const fetchAllowedEmails = async () => {
    try {
      const response = await axios.get('/admin/allowed-emails');
      setAllowedEmails(response.data.allowed_emails);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching allowed emails:', error);
      setLoading(false);
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post('/admin/allowed-emails', {
        email: newEmail,
        notes: notes
      });
      
      alert('Το email προστέθηκε επιτυχώς!');
      setShowModal(false);
      setNewEmail('');
      setNotes('');
      fetchAllowedEmails();
    } catch (error) {
      alert(error.response?.data?.error || 'Σφάλμα κατά την προσθήκη');
    }
  };

  const handleBulkAdd = async (e) => {
    e.preventDefault();
    
    const emails = bulkEmails
      .split('\n')
      .map(e => e.trim())
      .filter(e => e.length > 0);

    if (emails.length === 0) {
      alert('Παρακαλώ εισάγετε τουλάχιστον ένα email');
      return;
    }

    try {
      const response = await axios.post('/admin/allowed-emails/bulk', {
        emails: emails,
        notes: notes
      });
      
      const { added, skipped } = response.data;
      
      let message = `Προστέθηκαν ${added.length} emails`;
      if (skipped.length > 0) {
        message += `\n\nΠαραλείφθηκαν ${skipped.length}:`;
        skipped.slice(0, 5).forEach(s => {
          message += `\n- ${s.email}: ${s.reason}`;
        });
        if (skipped.length > 5) {
          message += `\n... και ${skipped.length - 5} ακόμα`;
        }
      }
      
      alert(message);
      setShowBulkModal(false);
      setBulkEmails('');
      setNotes('');
      fetchAllowedEmails();
    } catch (error) {
      alert(error.response?.data?.error || 'Σφάλμα κατά τη μαζική προσθήκη');
    }
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Είστε σίγουροι ότι θέλετε να αφαιρέσετε το email ${email} από τη λίστα;`)) {
      return;
    }

    try {
      await axios.delete(`/admin/allowed-emails/${id}`);
      alert('Το email αφαιρέθηκε επιτυχώς!');
      fetchAllowedEmails();
    } catch (error) {
      alert(error.response?.data?.error || 'Σφάλμα κατά τη διαγραφή');
    }
  };

  if (loading) {
    return <div className="loading">Φόρτωση...</div>;
  }

  return (
    <div className="whitelist-container">
      <div className="whitelist-header">
        <div>
          <h2>📧 Εγκεκριμένα Emails (Google SSO)</h2>
          <p className="subtitle">
            Μόνο τα emails σε αυτή τη λίστα μπορούν να συνδεθούν μέσω Google
          </p>
        </div>
        <div className="header-actions">
          <button onClick={() => setShowModal(true)} className="btn-primary">
            + Προσθήκη Email
          </button>
          <button onClick={() => setShowBulkModal(true)} className="btn-secondary">
            📋 Μαζική Προσθήκη
          </button>
        </div>
      </div>

      <div className="stats-card">
        <span className="stat-label">Σύνολο Εγκεκριμένων:</span>
        <span className="stat-value">{allowedEmails.length}</span>
      </div>

      <div className="whitelist-table-container">
        <table className="whitelist-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Σημειώσεις</th>
              <th>Προστέθηκε από</th>
              <th>Ημερομηνία</th>
              <th>Ενέργειες</th>
            </tr>
          </thead>
          <tbody>
            {allowedEmails.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">
                  <div>
                    <p>⚠️ Δεν υπάρχουν εγκεκριμένα emails</p>
                    <p className="hint">Προσθέστε emails για να επιτρέψετε πρόσβαση μέσω Google SSO</p>
                  </div>
                </td>
              </tr>
            ) : (
              allowedEmails.map(item => (
                <tr key={item.id}>
                  <td className="email-cell">
                    <strong>{item.email}</strong>
                  </td>
                  <td>{item.notes || '-'}</td>
                  <td>{item.added_by_name || 'System'}</td>
                  <td>{new Date(item.created_at).toLocaleDateString('el-GR')}</td>
                  <td>
                    <button
                      onClick={() => handleDelete(item.id, item.email)}
                      className="btn-delete-small"
                      title="Αφαίρεση"
                    >
                      🗑️ Αφαίρεση
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Single Email Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Προσθήκη Εγκεκριμένου Email</h2>
            
            <form onSubmit={handleAddEmail}>
              <div className="form-group">
                <label>Email (Gmail) *</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="example@gmail.com"
                  required
                />
                <small>Μόνο Gmail addresses για Google SSO</small>
              </div>

              <div className="form-group">
                <label>Σημειώσεις (προαιρετικό)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="π.χ. Υπεύθυνος Marketing, Συνεργάτης, κλπ"
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  Προσθήκη
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Ακύρωση
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Add Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <h2>Μαζική Προσθήκη Emails</h2>
            
            <form onSubmit={handleBulkAdd}>
              <div className="form-group">
                <label>Emails (ένα ανά γραμμή) *</label>
                <textarea
                  value={bulkEmails}
                  onChange={(e) => setBulkEmails(e.target.value)}
                  placeholder="user1@gmail.com&#10;user2@gmail.com&#10;user3@gmail.com"
                  rows="10"
                  required
                />
                <small>Εισάγετε ένα email ανά γραμμή</small>
              </div>

              <div className="form-group">
                <label>Σημειώσεις για όλα (προαιρετικό)</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="π.χ. Ομάδα Marketing Q1 2025"
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  Προσθήκη Όλων
                </button>
                <button type="button" onClick={() => setShowBulkModal(false)} className="btn-secondary">
                  Ακύρωση
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default WhitelistManager;
